import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Stack,
  Chip,
  Tab,
  Tabs,
  Dialog,
  Breadcrumbs,
  Link,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  NavigateNext as NavigateNextIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';

const STATUS_COLORS = [
  { value: '#6B7280', label: 'Gray' },
  { value: '#EF4444', label: 'Red' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#10B981', label: 'Green' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#0EA5E9', label: 'Sky' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#A855F7', label: 'Violet' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#F43F5E', label: 'Rose' },
  { value: '#78716C', label: 'Stone' },
];

interface WorkflowStatus {
  id?: string;
  name: string;
  color: string;
  orderIndex: number;
  canDelete?: boolean;
}

interface WorkflowCategory {
  id?: string;
  name: string;
  description?: string;
  orderIndex: number;
}

const BrandStatusManager = ({
  statuses,
  setStatuses,
  workflowId,
}: {
  statuses: WorkflowStatus[];
  setStatuses: (statuses: WorkflowStatus[]) => void;
  workflowId: string;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#3B82F6');
  const [editStatusName, setEditStatusName] = useState('');
  const [editStatusColor, setEditStatusColor] = useState('#3B82F6');

  const handleAddStatus = async () => {
    if (!newStatusName.trim()) {
      notify('Status name is required', { type: 'warning' });
      return;
    }

    const maxOrder = statuses.length > 0 ? Math.max(...statuses.map(s => s.orderIndex)) : 0;

    try {
      const { data: newStatus } = await dataProvider.create('brand-workflows', {
        data: {
          name: newStatusName,
          color: newStatusColor,
        },
        meta: {
          url: `brand-workflows/${workflowId}/statuses`,
        },
      });

      setStatuses([...statuses, newStatus]);
      setNewStatusName('');
      setNewStatusColor('#3B82F6');
      setAddDialogOpen(false);
      notify('Status added successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to add status', { type: 'error' });
    }
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditStatusName(statuses[index].name);
    setEditStatusColor(statuses[index].color);
    setEditDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (editingIndex === null) return;
    if (!editStatusName.trim()) {
      notify('Status name is required', { type: 'warning' });
      return;
    }

    const statusToUpdate = statuses[editingIndex];

    try {
      await dataProvider.update('brand-workflows', {
        id: statusToUpdate.id,
        data: {
          name: editStatusName,
          color: editStatusColor,
        },
        meta: {
          url: `brand-workflows/statuses/${statusToUpdate.id}`,
        },
      });

      const newStatuses = [...statuses];
      newStatuses[editingIndex] = {
        ...newStatuses[editingIndex],
        name: editStatusName,
        color: editStatusColor,
      };
      setStatuses(newStatuses);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Status updated successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to update status', { type: 'error' });
    }
  };

  const handleDeleteStatus = async () => {
    if (editingIndex === null) return;

    const statusToDelete = statuses[editingIndex];

    try {
      await dataProvider.delete('brand-workflows', {
        id: statusToDelete.id,
        meta: {
          url: `brand-workflows/statuses/${statusToDelete.id}`,
        },
      });

      const newStatuses = statuses.filter((_, i) => i !== editingIndex);
      setStatuses(newStatuses);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Status deleted successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to delete status', { type: 'error' });
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    let newOrderIndex: number;
    const destIndex = result.destination.index;

    if (destIndex === 0) {
      newOrderIndex = items[0].orderIndex / 2;
    } else if (destIndex === items.length - 1) {
      newOrderIndex = items[items.length - 2].orderIndex + 1;
    } else {
      const prevOrder = items[destIndex - 1].orderIndex;
      const nextOrder = items[destIndex + 1].orderIndex;
      newOrderIndex = (prevOrder + nextOrder) / 2;
    }

    reorderedItem.orderIndex = newOrderIndex;
    items[destIndex] = reorderedItem;

    setStatuses(items);

    try {
      await dataProvider.update('brand-workflows', {
        id: reorderedItem.id,
        data: { orderIndex: newOrderIndex },
        meta: {
          url: `brand-workflows/statuses/${reorderedItem.id}`,
        },
      });
      notify('Status order updated', { type: 'success' });
    } catch (error) {
      notify('Failed to update status order', { type: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
        >
          Add Status
        </Button>
      </Box>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Status Name"
              fullWidth
              value={newStatusName}
              onChange={(e) => setNewStatusName(e.target.value)}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={newStatusColor}
                onChange={(e) => setNewStatusColor(e.target.value)}
                label="Color"
              >
                {STATUS_COLORS.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color.value,
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddStatus} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Status</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Status Name"
              fullWidth
              value={editStatusName}
              onChange={(e) => setEditStatusName(e.target.value)}
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>Color</InputLabel>
              <Select
                value={editStatusColor}
                onChange={(e) => setEditStatusColor(e.target.value)}
                label="Color"
              >
                {STATUS_COLORS.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: color.value,
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteStatus} color="error">
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="statuses">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {statuses.map((status, index) => (
                <Draggable
                  key={status.id || index}
                  draggableId={status.id || `status-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 1,
                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <CardContent
                        sx={{
                          py: 1,
                          '&:last-child': { pb: 1 },
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => handleEditClick(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}>
                            <DragIcon sx={{ cursor: 'grab', color: 'text.secondary' }} />
                          </Box>

                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: status.color,
                              flexShrink: 0,
                            }}
                          />

                          <Typography sx={{ flex: 1 }}>{status.name}</Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {statuses.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No statuses defined. Add a status to get started.
        </Box>
      )}
    </Box>
  );
};

const BrandCategoryManager = ({
  categories,
  setCategories,
  workflowId,
}: {
  categories: WorkflowCategory[];
  setCategories: (categories: WorkflowCategory[]) => void;
  workflowId: string;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryDescription, setEditCategoryDescription] = useState('');

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      notify('Category name is required', { type: 'warning' });
      return;
    }

    const maxOrder = categories.length > 0 ? Math.max(...categories.map(c => c.orderIndex)) : 0;

    try {
      const { data: newCategory } = await dataProvider.create('brand-workflows', {
        data: {
          name: newCategoryName,
          description: newCategoryDescription,
        },
        meta: {
          url: `brand-workflows/${workflowId}/categories`,
        },
      });

      setCategories([...categories, newCategory]);
      setNewCategoryName('');
      setNewCategoryDescription('');
      setAddDialogOpen(false);
      notify('Category added successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to add category', { type: 'error' });
    }
  };

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditCategoryName(categories[index].name);
    setEditCategoryDescription(categories[index].description || '');
    setEditDialogOpen(true);
  };

  const handleUpdateCategory = async () => {
    if (editingIndex === null) return;
    if (!editCategoryName.trim()) {
      notify('Category name is required', { type: 'warning' });
      return;
    }

    const categoryToUpdate = categories[editingIndex];

    try {
      await dataProvider.update('brand-workflows', {
        id: categoryToUpdate.id,
        data: {
          name: editCategoryName,
          description: editCategoryDescription,
        },
        meta: {
          url: `brand-workflows/categories/${categoryToUpdate.id}`,
        },
      });

      const newCategories = [...categories];
      newCategories[editingIndex] = {
        ...newCategories[editingIndex],
        name: editCategoryName,
        description: editCategoryDescription,
      };
      setCategories(newCategories);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Category updated successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to update category', { type: 'error' });
    }
  };

  const handleDeleteCategory = async () => {
    if (editingIndex === null) return;

    const categoryToDelete = categories[editingIndex];

    try {
      await dataProvider.delete('brand-workflows', {
        id: categoryToDelete.id,
        meta: {
          url: `brand-workflows/categories/${categoryToDelete.id}`,
        },
      });

      const newCategories = categories.filter((_, i) => i !== editingIndex);
      setCategories(newCategories);
      setEditDialogOpen(false);
      setEditingIndex(null);
      notify('Category deleted successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to delete category', { type: 'error' });
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(categories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    let newOrderIndex: number;
    const destIndex = result.destination.index;

    if (destIndex === 0) {
      newOrderIndex = items[0].orderIndex / 2;
    } else if (destIndex === items.length - 1) {
      newOrderIndex = items[items.length - 2].orderIndex + 1;
    } else {
      const prevOrder = items[destIndex - 1].orderIndex;
      const nextOrder = items[destIndex + 1].orderIndex;
      newOrderIndex = (prevOrder + nextOrder) / 2;
    }

    reorderedItem.orderIndex = newOrderIndex;
    items[destIndex] = reorderedItem;

    setCategories(items);

    try {
      await dataProvider.update('brand-workflows', {
        id: reorderedItem.id,
        data: { orderIndex: newOrderIndex },
        meta: {
          url: `brand-workflows/categories/${reorderedItem.id}`,
        },
      });
      notify('Category order updated', { type: 'success' });
    } catch (error) {
      notify('Failed to update category order', { type: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          size="small"
        >
          Add Category
        </Button>
      </Box>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              fullWidth
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description"
              fullWidth
              value={newCategoryDescription}
              onChange={(e) => setNewCategoryDescription(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Category Name"
              fullWidth
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              autoFocus
            />
            <TextField
              label="Description"
              fullWidth
              value={editCategoryDescription}
              onChange={(e) => setEditCategoryDescription(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCategory} color="error">
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateCategory} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="categories">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {categories.map((category, index) => (
                <Draggable
                  key={category.id || index}
                  draggableId={category.id || `category-${index}`}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 1,
                        backgroundColor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                      }}
                    >
                      <CardContent
                        sx={{
                          py: 1,
                          '&:last-child': { pb: 1 },
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => handleEditClick(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box {...provided.dragHandleProps} onClick={(e) => e.stopPropagation()}>
                            <DragIcon sx={{ cursor: 'grab', color: 'text.secondary' }} />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 500 }}>{category.name}</Typography>
                            {category.description && (
                              <Typography variant="caption" color="text.secondary">
                                {category.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {categories.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
          No categories defined. Add a category to get started.
        </Box>
      )}
    </Box>
  );
};

export const BrandWorkflowShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [workflow, setWorkflow] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [statuses, setStatuses] = useState<WorkflowStatus[]>([]);
  const [categories, setCategories] = useState<WorkflowCategory[]>([]);
  const [basicInfoDialogOpen, setBasicInfoDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);

  // Fetch workflow on mount
  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        const { data } = await dataProvider.getOne('brand-workflows', { id: id! });
        setWorkflow(data);
        const sortedStatuses = [...(data.statuses || [])].sort(
          (a, b) => a.orderIndex - b.orderIndex
        );
        const sortedCategories = [...(data.categories || [])].sort(
          (a, b) => a.orderIndex - b.orderIndex
        );
        setStatuses(sortedStatuses);
        setCategories(sortedCategories);
        setLoading(false);
      } catch (error) {
        notify('Failed to load workflow', { type: 'error' });
        setLoading(false);
      }
    };

    if (id) {
      fetchWorkflow();
    }
  }, [id, dataProvider, notify]);

  const handleEditBasicInfo = () => {
    setEditName(workflow?.name || '');
    setEditDescription(workflow?.description || '');
    setEditIsActive(workflow?.isActive ?? true);
    setBasicInfoDialogOpen(true);
  };

  const handleUpdateBasicInfo = async () => {
    if (!editName.trim()) {
      notify('Workflow name is required', { type: 'warning' });
      return;
    }

    try {
      const updated = await dataProvider.update('brand-workflows', {
        id: workflow.id,
        data: {
          name: editName,
          description: editDescription,
          isActive: editIsActive,
        },
      });

      setWorkflow(updated.data);
      setBasicInfoDialogOpen(false);
      notify('Workflow updated successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to update workflow', { type: 'error' });
    }
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!workflow) {
    return <Typography>Workflow not found</Typography>;
  }

  const TabPanel = ({ children, value, index }: { children: React.ReactNode; value: number; index: number }) => (
    <Box hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/configuration/brand-workflows')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Brand Workflows
        </Link>
        <Typography color="text.primary">{workflow?.name || 'Workflow'}</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/configuration/brand-workflows')}
          variant="outlined"
          size="small"
        >
          Back
        </Button>
      </Box>

      {/* Basic Info Edit Dialog */}
      <Dialog open={basicInfoDialogOpen} onClose={() => setBasicInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Basic Info</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Workflow Name"
              fullWidth
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              required
              autoFocus
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={editIsActive} onChange={(e) => setEditIsActive(e.target.checked)} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBasicInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBasicInfo} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="Basic Info" />
        <Tab label="Statuses" />
        <Tab label="Categories" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box>
          <Card sx={{ p: 2, position: 'relative' }}>
            <IconButton
              onClick={handleEditBasicInfo}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'action.hover',
                '&:hover': {
                  bgcolor: 'action.selected',
                },
              }}
              title="Edit Basic Info"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{workflow?.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">{workflow?.description || 'No description'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={workflow?.isActive ? 'Active' : 'Inactive'}
                  color={workflow?.isActive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </Stack>
          </Card>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <BrandStatusManager statuses={statuses} setStatuses={setStatuses} workflowId={workflow?.id || ''} />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <BrandCategoryManager categories={categories} setCategories={setCategories} workflowId={workflow?.id || ''} />
      </TabPanel>
    </Box>
  );
};
